import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Badge,
  DialogActions,
  Select,
  DialogContent,
  DialogTitle,
  Dialog,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Menu,
  CircularProgress,
  Modal,
  MenuItem,
  Divider,
  Grow,
  AppBar,
  Toolbar,
  IconButton,
  TablePagination,
  TableCell,
  TableRow,
  TableContainer,
  TableBody,
  TableHead,
  Table,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  CssBaseline,
  Box,
  Paper,
  Popper,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import { FaFileAlt, FaClipboardList, FaReceipt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";
import axios from "axios";

import testlogo from "./testlogo.jpg";
import api from "../api/api";

const drawerWidth = 240; // width of the sidebar/drawer

const StudentDashboard = () => {
  /* ------------------------------------------------------------------ */
  /*                             STATE                                  */
  /* ------------------------------------------------------------------ */

  // Drawer: only toggled on mobile
  const [mobileOpen, setMobileOpen] = useState(false);

  // User data
  const [user, setUser] = useState(null);

  // Payments
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Grades dialog
  const [openPopup, setOpenPopup] = useState(false);
  const [grades, setGrades] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newNotificationReceived, setNewNotificationReceived] = useState(false);

  // Misc
  const [error, setError] = useState(null);
  const reportRef = useRef();

  const token = localStorage.getItem("access");

  /* ------------------------------------------------------------------ */
  /*                       RESPONSIVE HELPERS                            */
  /* ------------------------------------------------------------------ */

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  /* ------------------------------------------------------------------ */
  /*                        FETCH HELPERS                                */
  /* ------------------------------------------------------------------ */

  // User profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await api.get("/user-profile/");
        if (res.data?.id) setUser(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchUserProfile();
  }, []);

  // Payments
  useEffect(() => {
    const fetchPayments = async () => {
      if (!token) return;
      try {
        const res = await api.get("/api/users/payments/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPayments(res.data);
      } catch (err) {
        setError("Error fetching payments.");
      }
    };
    fetchPayments();
  }, [token]);

  // Period list
  useEffect(() => {
    const fetchPeriods = async () => {
      if (!token) return;
      try {
        const res = await api.get("/api/users/periods/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPeriods(res.data);
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch periods.",
        });
      }
    };
    fetchPeriods();
  }, [token]);

  // Notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await axios.get(
          "http://127.0.0.1:8000/api/users/notifications/",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setNotifications(res.data);
        const unread = res.data.filter((n) => !n.is_read).length;
        setUnreadCount(unread);
        setNewNotificationReceived(unread > 0);
      } catch (e) {
        console.error("Error fetching notifications:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.post(
        `http://127.0.0.1:8000/api/users/notifications/${id}/mark_as_read/`,
        { is_read: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (e) {
      console.error("Error marking notification:", e);
    }
  };

  /* ------------------------------------------------------------------ */
  /*                           HANDLERS                                  */
  /* ------------------------------------------------------------------ */

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const handleViewGrades = () => {
    if (!selectedPeriod) return;
    setLoading(true);
    setTimeout(async () => {
      await fetchGrades(selectedPeriod);
      setLoading(false);
    }, 3000);
  };

  const fetchGrades = async (period) => {
    try {
      const res = await api.get("/api/users/student-averages/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { period },
      });
      if (res.data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No grades",
          text: "We have not published your grades.",
        });
      } else {
        setGrades(res.data);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch grades.",
      });
    }
  };

  /* Close popup handler - FIXED: defined here */
  const closePopupHandler = () => {
    setOpenPopup(false);
  };

  /* ------------------------------------------------------------------ */
  /*                       PDF DOWNLOAD                                  */
  /* ------------------------------------------------------------------ */

  const downloadPDF = () => {
    const input = reportRef.current;
    if (!input) return;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 190;
      const pageHeight = pdf.internal.pageSize.height;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save("GradeSheet.pdf");
    });
  };

  /* ------------------------------------------------------------------ */
  /*                           RENDER                                    */
  /* ------------------------------------------------------------------ */

  if (!user) return <div>Loading...</div>;

  /* Drawer / sidebar content – shared by mobile + desktop -------------- */
  const drawerContent = (
    <Box sx={{ pt: 2, bgcolor: "#2b466e", height: "100%" }}>
      {/* Profile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          pb: 2,
          borderBottom: "1px solid #4F6D7A",
        }}
      >
        <Avatar
          src={
            user.profile_image
              ? `http://127.0.0.1:8000${user.profile_image}`
              : "/default-profile.png"
          }
          sx={{ width: 90, height: 90, mb: 1, border: "3px solid #fff" }}
        />
        <Typography sx={{ color: "orange", fontWeight: "bold" }}>
          {user.username}
        </Typography>
        <Typography sx={{ color: "gray", fontSize: "0.85rem" }}>
          {user.email}
        </Typography>
        <Typography sx={{ color: "#1976d2", fontSize: "0.85rem" }}>
          {user.user_type}
        </Typography>
      </Box>

      {/* Links */}
      <List>
        <ListItem
          component={Link}
          to="/payment-plan"
          sx={{
            "&:hover": { bgcolor: "#003366" },
            borderBottom: "1px solid #4F6D7A",
          }}
        >
          <FaFileAlt style={{ marginRight: 10, color: "orange" }} />
          <ListItemText primary="Attendance" sx={{ color: "#fff" }} />
        </ListItem>

        <ListItem
          onClick={() => setOpenPopup(true)}
          sx={{
            "&:hover": { bgcolor: "#003366" },
            borderBottom: "1px solid #4F6D7A",
          }}
        >
          <FaClipboardList style={{ marginRight: 10, color: "orange" }} />
          <ListItemText primary="Grade Report" sx={{ color: "#fff" }} />
        </ListItem>

        <ListItem
          component={Link}
          to="/receipt"
          sx={{
            "&:hover": { bgcolor: "#003366" },
            borderBottom: "1px solid #4F6D7A",
          }}
        >
          <FaReceipt style={{ marginRight: 10, color: "orange" }} />
          <ListItemText primary="Subject" sx={{ color: "#fff" }} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />

      {/* Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            position: "fixed",
            zIndex: 1300,
            top: 64,
            height: "calc(100% - 64px)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: "100%",
          ml: isMobile ? 0 : `${drawerWidth}px`,
          overflow: "auto",
        }}
      >
        {/* AppBar */}
        <AppBar
          position="fixed"
          sx={{
            bgcolor: "#2b466e",
            width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
            ml: isMobile ? 0 : `${drawerWidth}px`,
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap>
              Student Dashboard
            </Typography>

            <IconButton
              color="inherit"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ ml: "auto" }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton color="inherit">
              <MessageIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Dashboard Body */}
        <Toolbar /> {/* spacer under AppBar */}
        <Box sx={{ mt: 1 }}>
          {/* Profile Card */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              p: 2,
              bgcolor: "#f5f5f5",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} display="flex" justifyContent="center">
                <Avatar
                  alt={user.name}
                  src={
                    user.profile_image
                      ? `http://127.0.0.1:8000${user.profile_image}`
                      : "/default.jpg"
                  }
                  sx={{ width: 100, height: 100 }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={9}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Typography variant="h6" fontWeight="bold">
                  {user.username}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.user_type}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body1" gutterBottom>
              {user.bio}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Button variant="contained" color="primary">
                LogOut
              </Button>
              <Button variant="outlined" color="secondary">
                View Attendance
              </Button>
            </Box>
          </Paper>

          {/* Payment Table & Other widgets */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
            }}
          >
            {/* Payments */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              {payments.length > 0 ? (
                <TableContainer
                  component={Paper}
                  sx={{ maxWidth: 600, mx: "auto", overflowX: "auto" }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#FFEB3B" }}>
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Payment Details
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        {[
                          "Semester",
                          "Class",
                          "T/Fee",
                          "A/Paid",
                          "Balance",
                          "Date",
                        ].map((h) => (
                          <TableCell
                            key={h}
                            sx={{ bgcolor: "#0D1B2A", color: "#fff" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{p.semester}</TableCell>
                            <TableCell>{p.student_class_name}</TableCell>
                            <TableCell>${p.total_fee}</TableCell>
                            <TableCell>${p.amount_paid}</TableCell>
                            <TableCell
                              sx={{
                                color: p.balance_fee > 0 ? "red" : "green",
                              }}
                            >
                              ${p.balance_fee}
                            </TableCell>
                            <TableCell>
                              {new Date(p.date).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={payments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    bgcolor: "red",
                    color: "#fff",
                    p: 2,
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <MoneyOffIcon sx={{ fontSize: 50, mb: 1 }} />
                  <Typography>No fee has been paid by you!</Typography>
                </Box>
              )}
            </Box>

            {/* You can place chat / other widgets here */}
          </Box>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            elevation: 3,
            sx: { width: 300, maxHeight: 400, overflowY: "auto", borderRadius: 2 },
          }}
          TransitionComponent={Grow}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="h6" align="center">
              Notifications
            </Typography>
            <Divider />
          </Box>
          <List>
            {notifications.length > 0 ? (
              notifications.map((n) => (
                <ListItem
                  key={n.id}
                  divider
                  onClick={() => markAsRead(n.id)}
                  sx={{
                    bgcolor: n.is_read ? "inherit" : "#f5f5f5",
                    fontWeight: n.is_read ? "normal" : "bold",
                    "&:hover": { bgcolor: "#e0f7fa", cursor: "pointer" },
                  }}
                >
                  <ListItemText
                    primary={n.message}
                    secondary={new Date(n.created_at).toLocaleString()}
                  />
                </ListItem>
              ))
            ) : (
              <Typography sx={{ p: 2 }}>No notifications</Typography>
            )}
          </List>
        </Menu>

        {/* Grade Report Popup */}
 <Dialog open={openPopup} onClose={closePopupHandler} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: "#2b466e", color: "#fff" }}>
            Grade Report
          </DialogTitle>
  <DialogContent>
    {/* Period Selection Dropdown */}
    <FormControl fullWidth margin="normal">
      <InputLabel>Period</InputLabel>
      <Select label="Period" value={selectedPeriod} onChange={handlePeriodChange}>
        {periods.map((period) => (
          <MenuItem key={period.id} value={period.id}>
            {period.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Button
      variant="contained"
      color="primary"
      onClick={handleViewGrades}
      disabled={!selectedPeriod} // Disable if no period selected
      sx={{ mb: 2 }}
    >
      {loading ? "Loading..." : "View Grades"}
    </Button>

    {/* Loading Spinner */}
    {loading && (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <CircularProgress />
        <p style={{ marginTop: "10px", textAlign: "center" }}>
          GradeSheet Loading Please Wait!
        </p>
      </div>
    )}

    {/* Display Grades Table */}
    {!loading && grades.length > 0 && (
      <div style={{ marginBottom: "30px" }} ref={reportRef}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 2,
            gap: 2,
            padding: 2,
            backgroundColor: "#D3D3D3", // Light gray background color
            borderRadius: 2,
          }}
        >
          <img
            src={testlogo}
            alt="School Logo"
            style={{ width: 100, borderRadius: 8 }}
          />
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Jena William Educational Foundation
            </Typography>
            <Typography variant="subtitle1">
              Mt. Marclay, Monrovia, Liberia
            </Typography>
            <Typography variant="subtitle1">
              <strong>Student Periodic Report</strong>
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{ padding: 2 }}
        >
          <Typography variant="body1" align="left">
            <strong>Student:</strong> {grades[0].student_name}
          </Typography>
          <Typography variant="body1" align="left">
            <strong>Grade Class:</strong> {grades[0].grade_class_name}
          </Typography>
          <Typography variant="body1" align="left">
            <strong>Period:</strong> {grades[0].period_name}
          </Typography>
          <Typography variant="body1" align="left">
            <strong>Average:</strong> {grades[0].average_score}
          </Typography>
          <Typography variant="body1" align="left">
            <strong>Rank:</strong> {grades[0].rank}
          </Typography>
        </Box>

        <TableContainer
          sx={{ maxWidth: "500px", margin: "0 auto" }} /* Center and reduce width */
        >
          <Table
            size="small" /* Reduce row height */
            sx={{ borderCollapse: "collapse", width: "100%" }} /* Full width */
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    borderRight: "1px solid #ccc",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    padding: "4px 6px",
                    width: "50%",
                  }}
                >
                  Subject
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    borderLeft: "1px solid #ccc",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    padding: "4px 6px",
                    width: "50%",
                  }}
                >
                  Final Score
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grades[0].student_subjects.map((subject, index) => (
                <TableRow key={index}>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid #ccc",
                      fontSize: "0.95rem",
                      padding: "2px 4px",
                    }}
                  >
                    {subject.subject_name}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "0.95rem",
                      padding: "2px 4px",
                      color:
                        parseFloat(subject.formatted_final_score) >= 70
                          ? "blue"
                          : "black",
                      fontWeight:
                        parseFloat(subject.formatted_final_score) >= 70
                          ? "normal"
                          : "bold",
                      width: "50%",
                      borderLeft: "1px solid #ddd",
                      borderRight: "1px solid #ddd",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: subject.formatted_final_score,
                    }}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
          }}
        >
          <div>
            <strong>Remark:</strong> {grades[0].remark}
          </div>
        </div>

        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 3 }}>
          <Button variant="contained" color="primary" onClick={downloadPDF}>
            Download PDF
          </Button>
        </Box>
      </div>
    )}

    {/* Error message */}
    {error && <p style={{ color: "red" }}>Error: {error}</p>}
  </DialogContent>
  <DialogActions>
    <Button onClick={closePopupHandler} color="secondary">
      Close
    </Button>
  </DialogActions>
</Dialog>

      </Box>
    </Box>
  );
};

export default StudentDashboard;